import axios from "axios";
import {
  buildConsumerRuntimeHeaders,
  resolveConsumerRuntimeMarketDomain,
  resolveConsumerRuntimeMetadataUrl,
} from "./runtime-endpoints";

const toText = (value: unknown): string => String(value ?? "").trim();

const asObject = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

const joinUrl = (base: string, path: string): string => {
  const trimmedBase = toText(base).replace(/\/+$/, "");
  const trimmedPath = path.replace(/^\/+/, "");
  return `${trimmedBase}/${trimmedPath}`;
};

export type PlatformMetadataPayload = {
  sourceUrl: string;
  raw: Record<string, unknown>;
  platformCode: string;
  platformName: string;
  appName: string;
  description: string;
  supportEmail: string;
  publicDomain: string;
  appDomain: string;
  dashboardDomain: string;
  iconUrl: string;
  logoLightUrl: string;
  logoDarkUrl: string;
  primaryColor: string;
  secondaryColor: string;
};

export const defaultPlatformMetadata = (): PlatformMetadataPayload => ({
  sourceUrl: "",
  raw: {},
  platformCode: "",
  platformName: "",
  appName: "",
  description: "",
  supportEmail: "",
  publicDomain: "",
  appDomain: "",
  dashboardDomain: "",
  iconUrl: "",
  logoLightUrl: "",
  logoDarkUrl: "",
  primaryColor: "#0f172a",
  secondaryColor: "#1d4ed8",
});

export const isPlatformMetadataValid = (payload: PlatformMetadataPayload | null | undefined): boolean => {
  if (!payload) return false;
  const hasIdentity = Boolean(toText(payload.platformCode) || toText(payload.platformName));
  const hasSource = Boolean(toText(payload.sourceUrl));
  // Logos are optional personalization, not tenant identity. A domain with a
  // valid canonical snapshot must remain usable while optional assets are
  // absent or still propagating.
  return hasIdentity && hasSource;
};

export const metadataStorageKey = "consumerRuntimePlatformMetadata";

export const derivePlatformMetadata = (rawInput: unknown, sourceUrl = ""): PlatformMetadataPayload => {
  const root = asObject(rawInput);
  const data = asObject(root.data);
  const appPlatform = asObject(root.appPlatform || data.appPlatform || root.platform || data.platform || root.branding || data.branding);
  const platformInfo = asObject(root.platformInfo || data.platformInfo);
  const marketplaceInfo = asObject(root.marketplace || data.marketplace);
  const domains = asObject(platformInfo.domains);
  const contacts = asObject(platformInfo.contacts);
  const designTokens = asObject(platformInfo.designTokens);
  const seo = asObject(root.seo || data.seo || appPlatform.seo || platformInfo.seo);

  const platformName =
    toText(appPlatform.name) ||
    toText(appPlatform.platformName) ||
    toText(asObject(root.platform).name) ||
    toText(platformInfo.displayName) ||
    toText(platformInfo.platformCode) ||
    toText(root.title) ||
    toText(root.name);

  const appName =
    toText(root.appName) ||
    toText(data.appName) ||
    toText(appPlatform.appName) ||
    toText(appPlatform.displayName) ||
    toText(asObject(platformInfo.touch).appName) ||
    toText(root.title) ||
    toText(root.name) ||
    platformName;

  const description =
    toText(seo.description) ||
    toText(appPlatform.description) ||
    toText(platformInfo.description) ||
    toText(root.description) ||
    toText(data.description);

  const rawIconUrl =
    toText(appPlatform.iconImage) ||
    toText(appPlatform.squareLogoImage) ||
    toText(appPlatform.iconUrl) ||
    toText(appPlatform.logo_url) ||
    toText(asObject(root.platform).logo_url);

  const rawLogoLightUrl =
    toText(appPlatform.lightLogoImage) ||
    toText(appPlatform.logoLightUrl) ||
    toText(appPlatform.horizontalLogoImage) ||
    toText(appPlatform.logo_url) ||
    toText(asObject(root.platform).logo_url) ||
    rawIconUrl;

  const rawLogoDarkUrl =
    toText(appPlatform.horizontalLogoImage) ||
    toText(appPlatform.logoDarkUrl) ||
    toText(appPlatform.darkLogoImage) ||
    toText(appPlatform.logo_url) ||
    toText(asObject(root.platform).logo_url) ||
    rawLogoLightUrl;

  const resolveAssetUrl = (value: string): string => {
    const clean = toText(value);
    if (!clean) return "";
    if (/^https?:\/\//i.test(clean)) return clean;
    try {
      const origin = new URL(sourceUrl).origin;
      return joinUrl(origin, clean);
    } catch {
      return clean;
    }
  };

  const iconUrl = resolveAssetUrl(rawIconUrl);
  const logoLightUrl = resolveAssetUrl(rawLogoLightUrl);
  const logoDarkUrl = resolveAssetUrl(rawLogoDarkUrl);

  const resolvedPlatformCode =
    toText(platformInfo.platformCode) ||
    toText(appPlatform.platformCode) ||
    toText(asObject(root.platform).code) ||
    toText(asObject(root.platform).name).toLowerCase().replace(/\s+/g, "-") ||
    toText(marketplaceInfo.host) ||
    toText(root.domain);

  return {
    sourceUrl: toText(sourceUrl),
    raw: root,
    platformCode: resolvedPlatformCode,
    platformName,
    appName,
    description,
    supportEmail: toText(contacts.supportEmail) || toText(appPlatform.contactEmail),
    publicDomain:
      toText(domains.publicDomain) ||
      toText(appPlatform.publicDomain) ||
      toText(marketplaceInfo.displayHost) ||
      toText(root.domain),
    appDomain:
      toText(domains.appDomain) ||
      toText(appPlatform.appDomain) ||
      toText(marketplaceInfo.host) ||
      toText(root.domain),
    dashboardDomain: toText(domains.dashboardDomain) || toText(appPlatform.dashboardDomain),
    iconUrl,
    logoLightUrl,
    logoDarkUrl,
    primaryColor: toText(appPlatform.mainColorCode) || toText(designTokens.primaryColor) || "#0f172a",
    secondaryColor: toText(appPlatform.altColorCode) || toText(designTokens.secondaryColor) || "#1d4ed8",
  };
};

export const resolvePlatformMetadataUrl = (sellingPointId?: string): string => {
  return resolveConsumerRuntimeMetadataUrl(sellingPointId);
};

export const resolveCurrentHostMetadataUrl = (): string => {
  if (import.meta.env.DEV) return "/dev-platform-metadata.json";
  // Activation has no connection UUID yet, so the protected Qommerce API
  // metadata route cannot be used here. Every deployed runtime host exposes
  // its public platform metadata at this same-origin endpoint.
  return "/metadata";
};

type FetchPlatformMetadataOptions = {
  fresh?: boolean;
  explicitMarketDomain?: string;
};

export const fetchPlatformMetadataFromUrl = async (
  url: string,
  options?: FetchPlatformMetadataOptions
): Promise<PlatformMetadataPayload | null> => {
  const cleanUrl = toText(url);
  if (!cleanUrl) return null;
  const response = await axios.get(cleanUrl, {
    timeout: 10_000,
    headers: buildConsumerRuntimeHeaders(undefined, options),
    params: { _ts: Date.now() },
  });
  const payload = derivePlatformMetadata(response.data, cleanUrl);
  return isPlatformMetadataValid(payload) ? payload : null;
};

export const fetchPlatformMetadata = async (
  sellingPointId?: string,
  options?: FetchPlatformMetadataOptions
): Promise<PlatformMetadataPayload | null> => {
  const url = resolvePlatformMetadataUrl(sellingPointId);
  return fetchPlatformMetadataFromUrl(url, options);
};

export const persistPlatformMetadata = (payload: PlatformMetadataPayload | null) => {
  try {
    if (!payload) {
      localStorage.removeItem(metadataStorageKey);
      window.dispatchEvent(new CustomEvent("platform-metadata:updated"));
      return;
    }
    localStorage.setItem(metadataStorageKey, JSON.stringify(payload));
    localStorage.setItem("platformName", payload.platformName);
    if (payload.appName) localStorage.setItem("platformAppName", payload.appName);
    if (payload.iconUrl) localStorage.setItem("platformIcon", payload.iconUrl);
    else localStorage.removeItem("platformIcon");
    if (payload.logoLightUrl) localStorage.setItem("platformLogo", payload.logoLightUrl);
    else localStorage.removeItem("platformLogo");
    window.dispatchEvent(new CustomEvent("platform-metadata:updated"));
  } catch {
    // ignore storage failures
  }
};

export const readPlatformMetadata = (): PlatformMetadataPayload => {
  try {
    const raw = localStorage.getItem(metadataStorageKey);
    if (!raw) return defaultPlatformMetadata();
    const stored = asObject(JSON.parse(raw));
    const normalized = { ...defaultPlatformMetadata(), ...stored } as PlatformMetadataPayload;
    const payload = isPlatformMetadataValid(normalized)
      ? normalized
      : { ...defaultPlatformMetadata(), ...derivePlatformMetadata(stored) };
    return isPlatformMetadataValid(payload) ? payload : defaultPlatformMetadata();
  } catch {
    return defaultPlatformMetadata();
  }
};

export const resolveFreshSetupMarketDomain = (): string => {
  return resolveConsumerRuntimeMarketDomain({ fresh: true });
};

const upsertMetaTag = (
  key: "name" | "property",
  value: string,
  content: string
): HTMLMetaElement | null => {
  if (typeof document === "undefined") return null;
  const cleanValue = toText(value);
  if (!cleanValue) return null;
  let tag = document.head.querySelector(`meta[${key}="${cleanValue}"]`) as HTMLMetaElement | null;
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(key, cleanValue);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
  return tag;
};

const upsertLinkTag = (rel: string, href: string): HTMLLinkElement | null => {
  if (typeof document === "undefined") return null;
  const cleanRel = toText(rel);
  const cleanHref = toText(href);
  if (!cleanRel || !cleanHref) return null;
  let link = document.head.querySelector(`link[rel="${cleanRel}"]`) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement("link");
    link.rel = cleanRel;
    document.head.appendChild(link);
  }
  link.href = cleanHref;
  return link;
};

export const applyPlatformMetadataToDocument = (
  payload: PlatformMetadataPayload | null | undefined,
  fallbackTitle = "Kiosk"
): void => {
  if (typeof document === "undefined") return;
  const metadata = payload && isPlatformMetadataValid(payload) ? payload : defaultPlatformMetadata();
  const storedAppName = (() => {
    try {
      return toText(window.localStorage.getItem("platformAppName"));
    } catch {
      return "";
    }
  })();
  const title = storedAppName || toText(metadata.appName) || toText(metadata.platformName) || fallbackTitle;
  const description =
    toText(metadata.description) ||
    [
      toText(metadata.platformName),
      toText(metadata.publicDomain || metadata.appDomain),
      toText(metadata.supportEmail),
    ]
      .filter(Boolean)
      .join(" · ");

  document.title = title;
  document.documentElement.style.setProperty("color-scheme", "dark");

  if (description) {
    upsertMetaTag("name", "description", description);
    upsertMetaTag("property", "og:description", description);
    upsertMetaTag("name", "twitter:description", description);
  }
  upsertMetaTag("name", "application-name", title);
  upsertMetaTag("name", "apple-mobile-web-app-title", title);
  upsertMetaTag("property", "og:title", title);
  upsertMetaTag("name", "twitter:title", title);
  upsertMetaTag("property", "og:site_name", toText(metadata.platformName) || title);
  upsertMetaTag("property", "og:type", "website");
  upsertMetaTag("name", "theme-color", toText(metadata.secondaryColor) || "#1d4ed8");
  upsertMetaTag("name", "msapplication-TileColor", toText(metadata.primaryColor) || "#0f172a");

  const iconUrl = toText(metadata.iconUrl);
  if (iconUrl) {
    upsertLinkTag("icon", iconUrl);
    upsertLinkTag("shortcut icon", iconUrl);
    upsertLinkTag("apple-touch-icon", iconUrl);
    upsertMetaTag("property", "og:image", iconUrl);
    upsertMetaTag("name", "twitter:image", iconUrl);
  }
};
